import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AppLoader from "../../../components/AppLoader";
import NavigationDrawer from "../../../components/NavigationDrawer";
import appcolors from "../../../constants/Colors";
import {
  getCreatorCampaignDetail,
  participateInCampaign,
} from "../../../services/creator/campaignApiFn";
import { handleOpenSnackBar } from "../../../utils";
import { CampaignParticipationAction, getKpiTiers } from "./CampaignCard";
import CampaignVideoSubmission from "./CampaignVideoSubmission";
import GlobalButton from "../../../components/CustomButton";

const SectionCard = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
    <Typography
      variant="h6"
      fontSize={"1rem"}
      fontWeight={600}
      color={appcolors.gunmetal}
      mb={1.5}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

function CreatorCampaignDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.userData.user);
  const { currentLanguage } = useSelector((state) => state.translationReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Package / product ke naam { en, ro } object me hote hain
  const localize = (value) =>
    value && typeof value === "object"
      ? value[currentLanguage] || value.en || value.ro || ""
      : value || "";

  const fetchCampaign = async () => {
    if (!userData?._id || !id) return;
    try {
      setIsLoading(true);
      const response = await getCreatorCampaignDetail(userData._id, id);
      if (response?.data?.status === "success") {
        setCampaign(response.data.data);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaign();
  }, [id, userData?._id]);

  const handleParticipate = async () => {
    try {
      setIsRequesting(true);
      const response = await participateInCampaign(userData._id, campaign._id);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        fetchCampaign();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsRequesting(false);
    }
  };

  const renderStarterBox = (pkg) => {
    const products = (pkg.products || []).filter((p) => p.productId);
    return (
      <Grid item xs={12} sm={6} lg={4} key={pkg._id}>
        <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
          {pkg.coverImage ? (
            <Box
              component="img"
              src={pkg.coverImage}
              alt={localize(pkg.name)}
              sx={{ width: "100%", height: 180, objectFit: "cover" }}
            />
          ) : (
            <Stack
              height={180}
              alignItems="center"
              justifyContent="center"
              bgcolor={"#f4f4f4"}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 56, color: "#bdbdbd" }} />
            </Stack>
          )}
          <CardContent>
            <Typography fontWeight={600} color={appcolors.gunmetal}>
              {localize(pkg.name)}
            </Typography>
            {localize(pkg.description) && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {localize(pkg.description)}
              </Typography>
            )}
            {products.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" fontWeight={600} mb={1}>
                  {t("starterBoxIncludes")}
                </Typography>
                <Stack gap={1}>
                  {products.map((item) => (
                    <Stack
                      key={`${item.productId._id}-${item.variantId || ""}`}
                      direction="row"
                      alignItems="center"
                      gap={1}
                    >
                      <Box
                        component="img"
                        src={item.productId.coverImage}
                        alt=""
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          objectFit: "cover",
                          bgcolor: "#f4f4f4",
                        }}
                      />
                      <Typography variant="body2" flex={1}>
                        {localize(item.productId.name)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        x{item.selectedQty}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />

      <GlobalButton
        btnText={t("back")}
        startIcon={<ArrowBackIcon />}
        handleClickBtn={() => navigate(-1)}
        styleProps={{ mb: 1.5, height: 40 }}
      />

      {campaign && (
        <Stack gap={2}>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, overflow: "hidden" }}
          >
            {campaign.coverImage ? (
              <Box
                component="img"
                src={campaign.coverImage}
                alt={campaign.name}
                sx={{
                  width: "100%",
                  height: { xs: 180, md: 280 },
                  objectFit: "cover",
                }}
              />
            ) : (
              <Stack
                height={{ xs: 140, md: 200 }}
                alignItems="center"
                justifyContent="center"
                bgcolor={"#f4f4f4"}
              >
                <CampaignOutlinedIcon sx={{ fontSize: 72, color: "#bdbdbd" }} />
              </Stack>
            )}
            <Stack
              p={2.5}
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              gap={2}
            >
              <Stack gap={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={appcolors.gunmetal}
                  >
                    {campaign.name}
                  </Typography>
                  <Chip size="small" label={t(campaign.type)} />
                  {campaign.status === "closed" && (
                    <Chip
                      size="small"
                      color="error"
                      label={t("campaignClosed")}
                    />
                  )}
                </Stack>
                <Stack direction="row" gap={3} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    {t("maxCreators")}:{" "}
                    {campaign.maxCreators
                      ? `${campaign.acceptedParticipants ?? 0}/${campaign.maxCreators}`
                      : t("unlimited")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("commission")}: {campaign.commission ?? 0}%
                  </Typography>
                  {campaign.publishedAt && (
                    <Typography variant="body2" color="text.secondary">
                      {t("publishedOn")}:{" "}
                      {moment(campaign.publishedAt).format("DD/MM/YYYY")}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              <CampaignParticipationAction
                campaign={campaign}
                onParticipate={handleParticipate}
                isRequesting={isRequesting}
              />
            </Stack>
          </Paper>

          <SectionCard title={t("kpis")}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("platform")}</TableCell>
                    <TableCell>{t("minViews")}</TableCell>
                    <TableCell>{t("maxViews")}</TableCell>
                    <TableCell>{t("timeFrame")}</TableCell>
                    <TableCell>{t("budget")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(campaign.kpis || []).map((kpi) => {
                    const tiers = getKpiTiers(kpi);
                    return tiers.map((tier, tierIndex) => (
                      <TableRow key={`${kpi.platform}-${tierIndex}`}>
                        {tierIndex === 0 && (
                          <TableCell
                            rowSpan={tiers.length}
                            sx={{ fontWeight: 600, verticalAlign: "top" }}
                          >
                            {t(kpi.platform)}
                          </TableCell>
                        )}
                        <TableCell>
                          {Number(tier.minViews).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {Number(tier.maxViews).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {t("campaignDaysCount", {
                            count: tier.timeFrameDays,
                          })}
                        </TableCell>
                        <TableCell>
                          {Number(tier.budget || 0).toLocaleString()} RON
                        </TableCell>
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          {campaign.participation?.status === "accepted" && (
            <SectionCard title={t("yourCampaignVideo")}>
              <CampaignVideoSubmission
                campaign={campaign}
                creatorId={userData?._id}
                onUpdated={fetchCampaign}
              />
            </SectionCard>
          )}

          {(campaign.instructions || campaign.instructionFile?.url) && (
            <SectionCard title={t("instructions")}>
              {campaign.instructions && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  whiteSpace="pre-line"
                >
                  {campaign.instructions}
                </Typography>
              )}

              {campaign.instructionFile?.url && (
                <Stack gap={1.5} mt={campaign.instructions ? 2 : 0}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    gap={1.5}
                    border="1px solid #e5e7eb"
                    borderRadius={2}
                    p={1.5}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                      flex={1}
                      minWidth={0}
                    >
                      <PictureAsPdfOutlinedIcon
                        color="error"
                        fontSize="large"
                      />
                      <Stack minWidth={0}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {campaign.instructionFile.name || "instructions.pdf"}
                        </Typography>
                        {campaign.instructionFile.size > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {(
                              campaign.instructionFile.size /
                              (1024 * 1024)
                            ).toFixed(2)}{" "}
                            MB
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                    <Stack direction="row" gap={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowPdfPreview((prev) => !prev)}
                      >
                        {t(showPdfPreview ? "hidePreview" : "preview")}
                      </Button>
                    </Stack>
                  </Stack>

                  {showPdfPreview && (
                    <Box
                      component="iframe"
                      src={campaign.instructionFile.url}
                      title={campaign.instructionFile.name || "instructions"}
                      sx={{
                        width: "100%",
                        height: { xs: 420, md: 650 },
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                      }}
                    />
                  )}
                </Stack>
              )}
            </SectionCard>
          )}

          <SectionCard
            title={`${t("starterBoxes")} (${campaign.packages?.length || 0})`}
          >
            {campaign.packages?.length ? (
              <Grid container spacing={2}>
                {campaign.packages.map(renderStarterBox)}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("noStarterBoxes")}
              </Typography>
            )}
          </SectionCard>
        </Stack>
      )}
    </NavigationDrawer>
  );
}

export default CreatorCampaignDetail;
